// WHAT: Page component for Spotify integration (placeholder/mock)
// WHY:  Provides UI structure for future Spotify integration
// DOMAIN: Productivity → Spotify
// DEPENDENCIES: None (mock data)
// RISKS:
//   - NOTE: This is a placeholder with mock data - not functional
//   - NOTE: No actual Spotify API integration - needs implementation
//   - NOTE: UI elements are not interactive - buttons don't work

import React from "react";

/**
 * WHAT:
 *   Page component that displays Spotify integration interface with mock data
 *   for current playback, controls, recently played, and settings.
 *
 * WHY:
 *   Provides UI structure for future Spotify integration implementation.
 *
 * BEHAVIOR:
 *   - Displays mock current playback information
 *   - Shows playback controls (non-functional)
 *   - Lists recently played tracks (mock data)
 *   - Shows settings toggles (non-functional)
 *
 * RISKS:
 *   - NOTE: This is a placeholder with mock data - not functional
 *   - NOTE: No actual Spotify API integration - needs implementation
 *   - NOTE: UI elements are not interactive - buttons don't work
 *
 * @returns JSX.Element
 *
 */
export const SpotifyPage: React.FC = () => {
  return (
    <div className="p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-white mb-8">Spotify Integration</h1>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Current Playback */}
          <div className="bg-neutral-800 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Current Playback</h2>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-green-500 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">🎵</span>
                </div>
                <div>
                  <p className="font-medium text-white">Bohemian Rhapsody</p>
                  <p className="text-neutral-400">Queen</p>
                  <p className="text-sm text-neutral-500">A Night at the Opera</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="w-full bg-neutral-700 rounded-full h-2">
                  <div className="bg-green-500 h-2 rounded-full w-2/3" />
                </div>
                <div className="flex justify-between text-sm text-neutral-400">
                  <span>2:15</span>
                  <span>5:55</span>
                </div>
              </div>
            </div>
          </div>

          {/* Playback Controls */}
          <div className="bg-neutral-800 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Controls</h2>
            <div className="flex items-center justify-center gap-4">
              <button className="p-3 bg-neutral-700 hover:bg-neutral-600 rounded-full transition-colors">
                ⏮️
              </button>
              <button className="p-4 bg-green-500 hover:bg-green-600 rounded-full transition-colors">
                ⏸️
              </button>
              <button className="p-3 bg-neutral-700 hover:bg-neutral-600 rounded-full transition-colors">
                ⏭️
              </button>
            </div>
          </div>

          {/* Recently Played */}
          <div className="bg-neutral-800 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Recently Played</h2>
            <div className="space-y-3">
              {[
                {
                  artist: "Eagles",
                  time: "2 min ago",
                  title: "Hotel California",
                },
                {
                  artist: "Led Zeppelin",
                  time: "15 min ago",
                  title: "Stairway to Heaven",
                },
                { artist: "John Lennon", time: "1 hour ago", title: "Imagine" },
              ].map((track) => (
                <div
                  className="flex items-center gap-3 p-2 hover:bg-neutral-700 rounded-lg transition-colors"
                  key={`${track.title}-${track.artist}-${track.time}`}
                >
                  <div className="w-10 h-10 bg-neutral-600 rounded flex items-center justify-center">
                    🎵
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-white">{track.title}</p>
                    <p className="text-sm text-neutral-400">{track.artist}</p>
                  </div>
                  <span className="text-xs text-neutral-500">{track.time}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Settings */}
          <div className="bg-neutral-800 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Settings</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-white">Auto-play</span>
                <div className="w-12 h-6 bg-neutral-600 rounded-full relative">
                  <div className="w-5 h-5 bg-white rounded-full absolute top-0.5 left-0.5 transition-transform" />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white">Crossfade</span>
                <div className="w-12 h-6 bg-green-500 rounded-full relative">
                  <div className="w-5 h-5 bg-white rounded-full absolute top-0.5 right-0.5 transition-transform" />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white">Volume</span>
                <div className="w-24 bg-neutral-600 rounded-full h-2">
                  <div className="bg-green-500 h-2 rounded-full w-3/4" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
